import { FC, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, Divider } from '@mui/material';

import { useForm } from '@carcareers/contexts';
import { DEFAULT_POST_DURATION } from '@carcareers/utils';

interface DateRangeInputProps {
  nameStart: string;
  nameEnd: string;
  postDuration?: number;
}

export const DateRangeInput: FC<DateRangeInputProps> = ({ nameStart, nameEnd, postDuration = DEFAULT_POST_DURATION }) => {
  const { getData, setData } = useForm();
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(postDuration, 'day'));

  useEffect(() => {
    const today = dayjs();
    const storedStartDate = getData(nameStart);
    const storedEndDate = getData(nameEnd);

    try {
      // Check if the stored values are valid date strings
      if (storedStartDate && (typeof storedStartDate === 'string' || storedStartDate instanceof Date)) {
        const parsed = dayjs(storedStartDate);
        if (parsed.isValid()) {
          setStartDate(parsed);
        } else {
          setStartDate(today);
        }
      } else {
        setStartDate(today);
        setData(nameStart, today.toISOString());
      }

      if (storedEndDate && (typeof storedEndDate === 'string' || storedEndDate instanceof Date)) {
        const parsed = dayjs(storedEndDate);
        if (parsed.isValid()) {
          setEndDate(parsed);
        } else {
          setEndDate(today.add(DEFAULT_POST_DURATION, 'day'));
        }
      } else {
        setEndDate(today.add(DEFAULT_POST_DURATION, 'day'));
        setData(nameEnd, today.add(DEFAULT_POST_DURATION, 'day').toISOString());
      }
    } catch (error) {
      console.error('Error parsing date values:', error);
      setStartDate(today);
      setEndDate(today.add(DEFAULT_POST_DURATION, 'day'));
      setData(nameStart, today.toISOString());
      setData(nameEnd, today.add(DEFAULT_POST_DURATION, 'day').toISOString());
    }
  }, [nameStart, nameEnd, getData, setData]);

  const handleStartDateChange = (date: Dayjs | null) => {
    if (date && date.isValid()) {
      setStartDate(date);
      setData(nameStart, new Date(date.toString()));
    }
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    if (date && date.isValid()) {
      setEndDate(date);
      setData(nameEnd, new Date(date.toString()));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <DateTimePicker
          label="Start Date"
          value={startDate}
          onChange={handleStartDateChange}
          sx={{ width: '100%' }}
          slotProps={{
            textField: {
              InputProps: {
                sx: { borderRadius: 3 },
              },
            },
          }}
        />
        <Divider orientation="horizontal" flexItem sx={{ width: '10px', my: 'auto' }} />
        <DateTimePicker
          label="End Date"
          value={endDate}
          onChange={handleEndDateChange}
          sx={{ width: '100%' }}
          slotProps={{
            textField: {
              InputProps: {
                sx: { borderRadius: 3 },
              },
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeInput;
